import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AutomationAction,
  AutomationCondition,
  AutomationTrigger,
} from '@frankenstein/shared';
import { useGuildConfig } from '../../../lib/auditQueries.js';
import { useRoles } from '../../../lib/automodQueries.js';
import {
  useAutomations,
  useAutomationMutations,
  useCompileAutomation,
  type Automation,
} from '../../../lib/automationsQueries.js';
import { Breadcrumbs } from '../../../components/Breadcrumbs.js';
import { LanguageSwitcher } from '../../../components/LanguageSwitcher.js';
import { ChannelPicker } from '../../../components/ChannelPicker.js';

export const Route = createFileRoute('/guilds/$guildId/automations')({
  component: AutomationsPage,
});

const TRIGGER_TYPES = [
  'message_create',
  'member_join',
  'member_leave',
  'reaction_add',
  'role_added',
  'scheduled',
] as const;
const CONDITION_TYPES = [
  'channel_is',
  'has_role',
  'not_has_role',
  'account_age_lt_days',
  'content_matches',
  'not_bot',
] as const;
const ACTION_TYPES = [
  'send_message',
  'send_embed',
  'add_role',
  'remove_role',
  'timeout',
  'delete_message',
  'dm',
  'webhook',
] as const;

function defaultTrigger(type: string): AutomationTrigger {
  switch (type) {
    case 'reaction_add':
      return { type: 'reaction_add', emoji: null };
    case 'role_added':
      return { type: 'role_added', roleId: null };
    case 'scheduled':
      return { type: 'scheduled', cron: '0 9 * * *' };
    default:
      return { type } as AutomationTrigger;
  }
}
function defaultCondition(type: string): AutomationCondition {
  switch (type) {
    case 'channel_is':
      return { type: 'channel_is', channelId: '' };
    case 'has_role':
      return { type: 'has_role', roleId: '' };
    case 'not_has_role':
      return { type: 'not_has_role', roleId: '' };
    case 'account_age_lt_days':
      return { type: 'account_age_lt_days', days: 7 };
    case 'content_matches':
      return { type: 'content_matches', pattern: '', regex: false, caseSensitive: false };
    default:
      return { type: 'not_bot' };
  }
}
function defaultAction(type: string): AutomationAction {
  switch (type) {
    case 'send_embed':
      return { type: 'send_embed', channelId: null, title: '', body: '', color: null };
    case 'add_role':
      return { type: 'add_role', roleId: '' };
    case 'remove_role':
      return { type: 'remove_role', roleId: '' };
    case 'timeout':
      return { type: 'timeout', seconds: 600, reason: null };
    case 'delete_message':
      return { type: 'delete_message' };
    case 'dm':
      return { type: 'dm', text: '' };
    case 'webhook':
      return { type: 'webhook', url: '' };
    default:
      return { type: 'send_message', channelId: null, text: '' };
  }
}

interface Form {
  name: string;
  enabled: boolean;
  trigger: AutomationTrigger;
  conditions: AutomationCondition[];
  actions: AutomationAction[];
}
function fromAutomation(a: Automation): Form {
  return {
    name: a.name,
    enabled: a.enabled,
    trigger: a.trigger,
    conditions: a.conditions,
    actions: a.actions,
  };
}
const EMPTY: Form = {
  name: '',
  enabled: true,
  trigger: { type: 'message_create' },
  conditions: [],
  actions: [{ type: 'send_message', channelId: null, text: '' }],
};

function AutomationsPage() {
  const { t } = useTranslation();
  const { guildId } = Route.useParams();
  const list = useAutomations(guildId);
  const m = useAutomationMutations(guildId);
  const [editing, setEditing] = useState<{ id: string | null; form: Form } | null>(null);

  const rows = list.data ?? [];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Header guildId={guildId} />
      <main className="mx-auto max-w-4xl px-6 py-8">
        <div className="mb-4 flex items-center justify-between">
          <p className="flex items-center gap-2 text-sm text-zinc-400">
            {t('automations.subtitle')}
            <span className="rounded-full border border-indigo-700/60 bg-indigo-950/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-300">
              Pro
            </span>
          </p>
          <button
            type="button"
            onClick={() => {
              setEditing({ id: null, form: structuredClone(EMPTY) });
            }}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500"
          >
            {t('automations.new')}
          </button>
        </div>

        {list.isLoading ? (
          <p className="text-sm text-zinc-400">{t('common.loading')}</p>
        ) : rows.length === 0 ? (
          <p className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-8 text-center text-sm text-zinc-500">
            {t('automations.empty')}
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="px-4 py-2">{t('automations.col.name')}</th>
                  <th className="px-4 py-2">{t('automations.col.trigger')}</th>
                  <th className="px-4 py-2 text-center">{t('automations.col.enabled')}</th>
                  <th className="px-4 py-2 text-right">{t('automations.col.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60">
                {rows.map((a) => (
                  <tr key={a.id} className="hover:bg-zinc-900/40">
                    <td className="px-4 py-2 font-medium">{a.name}</td>
                    <td className="px-4 py-2 text-zinc-400">
                      {t(`automations.trigger.${a.triggerType}`, { defaultValue: a.triggerType })}
                      <span className="ml-2 text-xs text-zinc-600">
                        {a.conditions.length} cond · {a.actions.length} act
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
                        checked={a.enabled}
                        disabled={m.patch.isPending}
                        onChange={(e) => {
                          m.patch.mutate({ id: a.id, body: { enabled: e.target.checked } });
                        }}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing({ id: a.id, form: fromAutomation(a) });
                          }}
                          className="text-xs text-indigo-400 hover:text-indigo-300"
                        >
                          {t('automations.edit')}
                        </button>
                        <button
                          type="button"
                          disabled={m.remove.isPending}
                          onClick={() => {
                            m.remove.mutate(a.id);
                          }}
                          className="text-xs text-rose-400 hover:text-rose-300 disabled:opacity-50"
                        >
                          {t('common.remove')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {editing ? (
        <Editor
          guildId={guildId}
          initial={editing.form}
          onClose={() => {
            setEditing(null);
          }}
          onSave={(form) => {
            if (editing.id) m.patch.mutate({ id: editing.id, body: form });
            else m.create.mutate(form);
            setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
}

function Editor({
  guildId,
  initial,
  onClose,
  onSave,
}: {
  guildId: string;
  initial: Form;
  onClose: () => void;
  onSave: (f: Form) => void;
}) {
  const { t } = useTranslation();
  const roles = useRoles(guildId);
  const [f, setF] = useState<Form>(initial);
  const compile = useCompileAutomation(guildId);
  const [nl, setNl] = useState('');
  const set = (p: Partial<Form>): void => {
    setF((s) => ({ ...s, ...p }));
  };
  const valid = f.name.trim() !== '' && f.actions.length > 0;

  const roleSelect = (value: string, onChange: (v: string) => void, allowAny = false) => (
    <select
      className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
      }}
    >
      <option value="">{allowAny ? t('automations.anyRole') : t('automations.pickRole')}</option>
      {(roles.data ?? []).map((r) => (
        <option key={r.id} value={r.id}>
          {r.name}
        </option>
      ))}
    </select>
  );

  return (
    <Modal title={t('automations.editor.title')} onClose={onClose}>
      <div className="space-y-5">
        <Field label={t('automations.editor.name')}>
          <input
            type="text"
            value={f.name}
            maxLength={100}
            onChange={(e) => {
              set({ name: e.target.value });
            }}
            className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm"
          />
        </Field>

        {/* NL AUTHORING — describe it in words, AI fills the builder for review */}
        <div className="rounded-md border border-indigo-900/50 bg-indigo-950/20 p-3">
          <label className="mb-1 block text-xs font-medium text-indigo-300">
            {t('automations.nl.label')}
          </label>
          <textarea
            value={nl}
            placeholder={t('automations.nl.placeholder')}
            onChange={(e) => {
              setNl(e.target.value);
            }}
            className="min-h-14 w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
          />
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              disabled={nl.trim() === '' || compile.isPending}
              onClick={() => {
                compile.mutate(nl, {
                  onSuccess: (res) => {
                    setF(res.automation as Form);
                  },
                });
              }}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {compile.isPending ? t('automations.nl.generating') : t('automations.nl.generate')}
            </button>
            {compile.isError ? (
              <span className="text-xs text-rose-400">{t('automations.nl.error')}</span>
            ) : null}
          </div>
        </div>

        {/* TRIGGER */}
        <Section label={t('automations.editor.trigger')}>
          <select
            className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
            value={f.trigger.type}
            onChange={(e) => {
              set({ trigger: defaultTrigger(e.target.value) });
            }}
          >
            {TRIGGER_TYPES.map((tt) => (
              <option key={tt} value={tt}>
                {t(`automations.trigger.${tt}`, { defaultValue: tt })}
              </option>
            ))}
          </select>
          {f.trigger.type === 'reaction_add' ? (
            <input
              type="text"
              placeholder={t('automations.emojiAny')}
              value={f.trigger.emoji ?? ''}
              onChange={(e) => {
                set({ trigger: { type: 'reaction_add', emoji: e.target.value || null } });
              }}
              className="mt-2 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm"
            />
          ) : null}
          {f.trigger.type === 'role_added'
            ? roleSelect(
                f.trigger.roleId ?? '',
                (v) => {
                  set({ trigger: { type: 'role_added', roleId: v || null } });
                },
                true,
              )
            : null}
          {f.trigger.type === 'scheduled' ? (
            <input
              type="text"
              placeholder="0 9 * * *"
              value={f.trigger.cron}
              onChange={(e) => {
                set({ trigger: { type: 'scheduled', cron: e.target.value } });
              }}
              className="mt-2 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 font-mono text-sm"
            />
          ) : null}
        </Section>

        {/* CONDITIONS */}
        <Section label={t('automations.editor.conditions')}>
          <div className="space-y-2">
            {f.conditions.map((c, i) => (
              <Row
                key={i}
                onRemove={() => {
                  set({ conditions: f.conditions.filter((_, j) => j !== i) });
                }}
              >
                <select
                  className="rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
                  value={c.type}
                  onChange={(e) => {
                    const next = [...f.conditions];
                    next[i] = defaultCondition(e.target.value);
                    set({ conditions: next });
                  }}
                >
                  {CONDITION_TYPES.map((ct) => (
                    <option key={ct} value={ct}>
                      {t(`automations.condition.${ct}`, { defaultValue: ct })}
                    </option>
                  ))}
                </select>
                <ConditionFields
                  c={c}
                  guildId={guildId}
                  roleSelect={roleSelect}
                  onChange={(nc) => {
                    const next = [...f.conditions];
                    next[i] = nc;
                    set({ conditions: next });
                  }}
                />
              </Row>
            ))}
            <AddBtn
              label={t('automations.addCondition')}
              onClick={() => {
                set({ conditions: [...f.conditions, defaultCondition('not_bot')] });
              }}
            />
          </div>
        </Section>

        {/* ACTIONS */}
        <Section label={t('automations.editor.actions')}>
          <div className="space-y-2">
            {f.actions.map((a, i) => (
              <Row
                key={i}
                onRemove={
                  f.actions.length > 1
                    ? () => {
                        set({ actions: f.actions.filter((_, j) => j !== i) });
                      }
                    : undefined
                }
              >
                <select
                  className="rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
                  value={a.type}
                  onChange={(e) => {
                    const next = [...f.actions];
                    next[i] = defaultAction(e.target.value);
                    set({ actions: next });
                  }}
                >
                  {ACTION_TYPES.map((at) => (
                    <option key={at} value={at}>
                      {t(`automations.action.${at}`, { defaultValue: at })}
                    </option>
                  ))}
                </select>
                <ActionFields
                  a={a}
                  guildId={guildId}
                  roleSelect={roleSelect}
                  onChange={(na) => {
                    const next = [...f.actions];
                    next[i] = na;
                    set({ actions: next });
                  }}
                />
              </Row>
            ))}
            <AddBtn
              label={t('automations.addAction')}
              onClick={() => {
                set({ actions: [...f.actions, defaultAction('send_message')] });
              }}
            />
          </div>
        </Section>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={!valid}
            onClick={() => {
              onSave(f);
            }}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {t('automations.editor.save')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

type RoleSelect = (
  value: string,
  onChange: (v: string) => void,
  allowAny?: boolean,
) => React.ReactElement;

function ConditionFields({
  c,
  guildId,
  roleSelect,
  onChange,
}: {
  c: AutomationCondition;
  guildId: string;
  roleSelect: RoleSelect;
  onChange: (c: AutomationCondition) => void;
}) {
  if (c.type === 'channel_is')
    return (
      <ChannelPicker
        guildId={guildId}
        value={c.channelId}
        onChange={(v) => {
          onChange({ type: 'channel_is', channelId: v });
        }}
      />
    );
  if (c.type === 'has_role' || c.type === 'not_has_role')
    return roleSelect(c.roleId, (v) => {
      onChange({ type: c.type, roleId: v });
    });
  if (c.type === 'account_age_lt_days')
    return (
      <input
        type="number"
        min={0}
        max={3650}
        value={c.days}
        onChange={(e) => {
          onChange({ type: 'account_age_lt_days', days: Number(e.target.value) || 0 });
        }}
        className="w-24 rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
      />
    );
  if (c.type === 'content_matches')
    return (
      <div className="flex flex-1 flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder="pattern"
          value={c.pattern}
          onChange={(e) => {
            onChange({ ...c, pattern: e.target.value });
          }}
          className="min-w-32 flex-1 rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
        />
        <label className="flex items-center gap-1 text-xs text-zinc-400">
          <input
            type="checkbox"
            checked={c.regex}
            onChange={(e) => {
              onChange({ ...c, regex: e.target.checked });
            }}
          />
          regex
        </label>
      </div>
    );
  return null;
}

function ActionFields({
  a,
  guildId,
  roleSelect,
  onChange,
}: {
  a: AutomationAction;
  guildId: string;
  roleSelect: RoleSelect;
  onChange: (a: AutomationAction) => void;
}) {
  if (a.type === 'send_message')
    return (
      <div className="flex flex-1 flex-col gap-2">
        <ChannelPicker
          guildId={guildId}
          value={a.channelId ?? ''}
          onChange={(v) => {
            onChange({ ...a, channelId: v || null });
          }}
          placeholder="(trigger channel)"
        />
        <textarea
          value={a.text}
          maxLength={2000}
          placeholder="{user} …"
          onChange={(e) => {
            onChange({ ...a, text: e.target.value });
          }}
          className="min-h-16 w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
        />
      </div>
    );
  if (a.type === 'send_embed')
    return (
      <div className="flex flex-1 flex-col gap-2">
        <ChannelPicker
          guildId={guildId}
          value={a.channelId ?? ''}
          onChange={(v) => {
            onChange({ ...a, channelId: v || null });
          }}
          placeholder="(trigger channel)"
        />
        <input
          type="text"
          placeholder="title"
          value={a.title}
          onChange={(e) => {
            onChange({ ...a, title: e.target.value });
          }}
          className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
        />
        <textarea
          value={a.body}
          placeholder="body"
          onChange={(e) => {
            onChange({ ...a, body: e.target.value });
          }}
          className="min-h-16 w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
        />
      </div>
    );
  if (a.type === 'add_role' || a.type === 'remove_role')
    return roleSelect(a.roleId, (v) => {
      onChange({ type: a.type, roleId: v });
    });
  if (a.type === 'timeout')
    return (
      <input
        type="number"
        min={1}
        max={2419200}
        value={a.seconds}
        onChange={(e) => {
          onChange({ type: 'timeout', seconds: Number(e.target.value) || 1, reason: a.reason });
        }}
        className="w-28 rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
      />
    );
  if (a.type === 'dm')
    return (
      <textarea
        value={a.text}
        placeholder="{user} …"
        onChange={(e) => {
          onChange({ type: 'dm', text: e.target.value });
        }}
        className="min-h-16 flex-1 rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
      />
    );
  if (a.type === 'webhook')
    return (
      <input
        type="url"
        placeholder="https://…"
        value={a.url}
        onChange={(e) => {
          onChange({ type: 'webhook', url: e.target.value });
        }}
        className="flex-1 rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
      />
    );
  return null;
}

function Row({
  children,
  onRemove,
}: {
  children: React.ReactNode;
  onRemove?: (() => void) | undefined;
}) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-zinc-800/70 bg-zinc-950/40 p-2">
      <div className="flex flex-1 flex-wrap items-start gap-2">{children}</div>
      <button
        type="button"
        disabled={!onRemove}
        onClick={onRemove}
        className="rounded-md border border-zinc-800 px-2 py-1 text-sm text-zinc-400 hover:bg-zinc-800 disabled:opacity-30"
      >
        −
      </button>
    </div>
  );
}
function AddBtn({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs text-indigo-400 hover:text-indigo-300"
    >
      + {label}
    </button>
  );
}
function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">{label}</p>
      {children}
    </div>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs text-zinc-400">{label}</label>
      {children}
    </div>
  );
}
function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[88vh] w-full max-w-xl overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-950 p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-200">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Header({ guildId }: { guildId: string }) {
  const { t } = useTranslation();
  const config = useGuildConfig(guildId);
  return (
    <header className="border-b border-zinc-800">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
        <Link to="/" className="shrink-0">
          <img src="/icon.png" alt="Frankenstein" className="h-6 w-6" />
        </Link>
        <Breadcrumbs
          items={[
            { label: t('app.name'), to: '/' },
            { label: config.data?.name ?? guildId, to: '/guilds/$guildId', params: { guildId } },
            { label: t('automations.title') },
          ]}
        />
        <div className="ml-auto">
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}
